import React from "react";
import {
  Box,
  Button,
  ColumnLayout,
  Container,
  ExpandableSection,
  Header,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-react";

const RATING_COLORS = {
  Green: '#1d8102',
  Yellow: '#d4a017',
  Red: '#d13212',
};

const RatingBadge = ({ rating }) => {
  const color = RATING_COLORS[rating] || '#5f6b7a';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
      <span style={{
        width: '14px', height: '14px', borderRadius: '50%',
        backgroundColor: color, display: 'inline-block',
      }} />
      <span style={{ fontWeight: 'bold', color }}>{rating}</span>
    </span>
  );
};

const ChecklistStatus = ({ status }) => {
  if (status === 'PASS') return <StatusIndicator type="success">Pass</StatusIndicator>;
  if (status === 'FAIL') return <StatusIndicator type="error">Fail</StatusIndicator>;
  return <StatusIndicator type="info">{status}</StatusIndicator>;
};

const formatCurrency = (value) => {
  if (value == null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2,
  }).format(value);
};

const ArqaResultReport = ({ data }) => {
  if (!data) return null;
  const result = data.result || data;

  const draftEmail = () => {
    const itemName = data.item_name || result.item_name || result.account_description || result.account || '';
    const team = data.team || '';
    const year = data.year || '';
    const period = data.period || '';
    const recId = data.rec_id || result.reconciliation_id || '';
    const company = result.company || '';
    const account = result.account || '';
    const accountDesc = result.account_description || '';
    const overallRating = result.overall_rating || 'N/A';
    const improvementMetric = result.improvement_metric || 'N/A';
    const confidenceScore = result.confidence_score || 'N/A';

    const subject = `ARQA Peer Review: ${itemName} (Rec ID: ${recId}) | Period ${period}, ${year}`;

    // Build checklist summary
    const checklistItems = [
      { key: 'chk1_asd_source_identified', label: 'ASD Source Identified' },
      { key: 'chk2_asd_independent', label: 'ASD Independent' },
      { key: 'chk3_tieout_correct', label: 'Tie-out Correct' },
      { key: 'chk4_aging_transaction_date', label: 'Aging Transaction Date' },
      { key: 'chk5_aging_buckets_accurate', label: 'Aging Buckets Accurate' },
      { key: 'chk6_reconciling_items_complete', label: 'Reconciling Items Complete' },
      { key: 'chk7_action_plans_61plus', label: 'Action Plans 61+ Days' },
    ];

    const checklistLines = checklistItems.map((item, idx) => {
      const status = result[item.key] || 'N/A';
      const icon = status === 'PASS' ? '✅' : status === 'FAIL' ? '❌' : '⚠️';
      return `  ${icon} CHK${idx + 1} - ${item.label}: ${status}`;
    });

    // Build issues for FAIL items
    const failedChecks = checklistItems.filter(item => result[item.key] === 'FAIL');
    const issueLines = failedChecks.map(item => `  • ${item.label}: Requires attention and remediation.`);

    // Build recommendations based on failures
    const recommendations = [];
    if (result.chk7_action_plans_61plus === 'FAIL') {
      recommendations.push('  • Document action plans for all reconciling items aged 61+ days, including responsible owners and target dates.');
    }
    if (result.chk5_aging_buckets_accurate === 'FAIL') {
      recommendations.push('  • Review and correct aging bucket classifications to ensure transaction dates are accurately reflected.');
    }
    if (result.chk6_reconciling_items_complete === 'FAIL') {
      recommendations.push('  • Ensure all reconciling items are fully listed with complete supporting documentation and explanations.');
    }
    if (result.chk2_asd_independent === 'FAIL') {
      recommendations.push('  • Validate that the ASD source used is fully independent and that any variance to GL is explained and within materiality.');
    }
    if (result.chk1_asd_source_identified === 'FAIL') {
      recommendations.push('  • Identify and document a proper ASD source for the reconciliation.');
    }
    if (result.chk3_tieout_correct === 'FAIL') {
      recommendations.push('  • Correct the tie-out to ensure adjusted balances reconcile with $0 unexplained difference.');
    }
    if (result.chk4_aging_transaction_date === 'FAIL') {
      recommendations.push('  • Verify aging is based on original transaction dates rather than posting or clearing dates.');
    }

    const body = [
      `Hi,`,
      ``,
      `I have completed a review of ${company} - ${itemName} (Reconciliation ID: ${recId}) for Period ${period}, ${year}. Please see a summary of my assessment and key observations.`,
      ``,
      `PEER REVIEW OUTCOME`,
      `Final Rating: ${overallRating}`,
      ``,
      `Improvement Metric: ${improvementMetric}`,
      ``,
      `CHECKLIST RESULTS`,
      ...checklistLines,
      ``,
      `KEY ISSUES IDENTIFIED`,
      ...(issueLines.length > 0 ? issueLines : ['  None — all checks passed.']),
      ``,
      `Summary: ${result.summary || result.detailed_feedback || 'N/A'}`,
      ``,
      ...(recommendations.length > 0 ? [
        `RECOMMENDATIONS`,
        ...recommendations,
        ``,
      ] : []),
      `Confidence Score: ${confidenceScore}/100`,
      ``,
      `Please review the above and feel free to reach out with any questions or clarification needs.`,
      ``,
      `Best,`,
      `Raman`,
      ``,
      `On behalf of the Digital Peer Review Committee`,
    ].join('\n');

    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(body);
    window.open(`mailto:?subject=${encodedSubject}&body=${encodedBody}`, '_blank');
  };

  return (
    <SpaceBetween direction="vertical" size="l">
      {/* Header Section */}
      <Container header={<Header variant="h2" actions={<Button onClick={draftEmail} iconName="envelope">Draft Email</Button>}>
        {result.company} — Account {result.account}
      </Header>}>
        <ColumnLayout columns={4} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Overall Rating</Box>
            <RatingBadge rating={result.overall_rating} />
          </div>
          <div>
            <Box variant="awsui-key-label">Confidence Score</Box>
            <Box variant="h3">{result.confidence_score}%</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">GL Balance (USD)</Box>
            <Box>{formatCurrency(result.gl_balance_usd)}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Improvement Area</Box>
            <Box>{result.improvement_metric}</Box>
          </div>
        </ColumnLayout>
      </Container>

      {/* Account Details */}
      <Container header={<Header variant="h3">Account Details</Header>}>
        <ColumnLayout columns={3} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Account Description</Box>
            <Box>{result.account_description}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Product</Box>
            <Box>{result.product}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Reconciliation ID</Box>
            <Box>{result.reconciliation_id}</Box>
          </div>
        </ColumnLayout>
        <Box margin={{ top: 's' }}>
          <Box variant="awsui-key-label">Period</Box>
          <Box>{data.year}-{data.period} | Team: {data.team} | Rec ID: {data.rec_id}</Box>
        </Box>
      </Container>

      {/* Summary */}
      <Container header={<Header variant="h3">Summary</Header>}>
        <Box>{result.summary}</Box>
      </Container>

      {/* Detailed Feedback */}
      <Container header={<Header variant="h3">Detailed Feedback</Header>}>
        <Box>{result.detailed_feedback}</Box>
      </Container>

      {/* Checklist Overview */}
      <Container header={<Header variant="h3">Quality Checklist</Header>}>
        <ColumnLayout columns={4} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">1. ASD Source</Box>
            <ChecklistStatus status={result.chk1_asd_source_identified} />
          </div>
          <div>
            <Box variant="awsui-key-label">2. ASD Independent</Box>
            <ChecklistStatus status={result.chk2_asd_independent} />
          </div>
          <div>
            <Box variant="awsui-key-label">3. Tie-out Correct</Box>
            <ChecklistStatus status={result.chk3_tieout_correct} />
          </div>
          <div>
            <Box variant="awsui-key-label">4. Aging by Txn Date</Box>
            <ChecklistStatus status={result.chk4_aging_transaction_date} />
          </div>
          <div>
            <Box variant="awsui-key-label">5. Aging Buckets</Box>
            <ChecklistStatus status={result.chk5_aging_buckets_accurate} />
          </div>
          <div>
            <Box variant="awsui-key-label">6. Recon Items Complete</Box>
            <ChecklistStatus status={result.chk6_reconciling_items_complete} />
          </div>
          <div>
            <Box variant="awsui-key-label">7. Action Plans 61+</Box>
            <ChecklistStatus status={result.chk7_action_plans_61plus} />
          </div>
        </ColumnLayout>
      </Container>

      {/* Checklist Details */}
      {result.checklist_details && result.checklist_details.length > 0 && (
        <Container header={<Header variant="h3">Checklist Evidence</Header>}>
          <SpaceBetween direction="vertical" size="m">
            {result.checklist_details.map((item, idx) => {
              const headerColor = item.status === 'PASS' ? '#1d8102' : item.status === 'FAIL' ? '#d13212' : '#5f6b7a';
              const statusIcon = item.status === 'PASS' ? '✅' : item.status === 'FAIL' ? '❌' : '⚠️';
              return (
              <div key={idx} style={{ borderLeft: `4px solid ${headerColor}`, paddingLeft: '8px' }}>
                <ExpandableSection
                  headerText={
                    <span>
                      {item.checkpoint} — <span style={{ color: headerColor, fontWeight: 'bold', fontSize: '14px' }}>{statusIcon} {item.status}</span>
                    </span>
                  }
                  variant="footer"
                >
                  <Box>{item.evidence}</Box>
                </ExpandableSection>
              </div>
              );
            })}
          </SpaceBetween>
        </Container>
      )}
    </SpaceBetween>
  );
};

export default ArqaResultReport;
